/**
 * Curated tap-a-prop gags (v0.9 RFC ruling 8) — the close-up jokes. DATA, not
 * code: keyed by the visuals slot key; the office scene hit-tests stagetap
 * against live slots and opens the zoom modal with this copy.
 *
 * Satire rules (docs/SATIRE_IDEAS.md): the joke targets the INCENTIVE
 * STRUCTURE, never the people inside it; every joke names the real behavior.
 */

export interface TapGag {
  /** Modal heading. */
  title: string;
  /** Body lines, rendered as separate paragraphs (textContent-only). */
  lines: string[];
}

export const TAP_GAGS: Record<string, TapGag> = {
  // regulatory capture / self-accreditation
  "doll-certwall": {
    title: "WALL OF ACCREDITATION",
    lines: [
      "“Accredited by the National Board of Recovery Excellence, a subsidiary of this facility.”",
      "“CERTIFIED 2024* — *renewal is automatic upon payment.”",
      "“This certificate certifies the purchase of this certificate.”",
      "One is upside down. It is hung in compliance with hanging requirements.",
    ],
  },
  "doll-cert-one": {
    title: "FIRST ACCREDITATION",
    lines: ["Framed before it existed. The frame came first. The frame usually does."],
  },
  // HR theater
  "doll-portraits": {
    title: "EMPLOYEE OF THE MONTH",
    lines: [
      "Six months. Six frames. One face.",
      "“Nominations are reviewed by the Employee of the Month.”",
    ],
  },
  // compliance theater
  "doll-binders": {
    title: "POLICIES, VOL. I–VII",
    lines: [
      "Shrink-wrapped, as delivered.",
      "“Sealed for your protection. Opening a binder voids compliance.”",
    ],
  },
  // insurance denial logic
  "doll-counter": {
    title: "INTAKE TERMINAL",
    lines: [
      "A claim form, mid-denial.",
      "Reason for denial: “treatment was effective, therefore not medically necessary.”",
    ],
  },
  // deferred maintenance as a business model
  "doll-bucket": {
    title: "MAINTENANCE TICKET #0001",
    lines: [
      "STATUS: OPEN. Vendor quote: $40.",
      "Executive Detox Package featuring our signature waterfall: $400/wk.",
    ],
  },
  // out-of-network surprise
  "doll-gumball": {
    title: "GUMBALLS — $4.00",
    lines: ["Gum is out-of-network.", "Chewing may be billed separately."],
  },
  // ── detox ──
  "detox-incident-sign": {
    title: "DAYS WITHOUT INCIDENT: 3",
    lines: [
      "The number is written on paper taped over the counter.",
      "Under that paper: more paper.",
      "It has always been 3.",
    ],
  },
  // ── residential ──
  "resi-bulletin": {
    title: "COMMUNITY BOARD",
    lines: ["ride offered: one way.", "found: motivation. (claimed)", "roommate wanted. (mandatory)"],
  },
  "resi-payphone": {
    title: "PAYPHONE",
    lines: ["local calls: collect.", "the cord is load-bearing."],
  },
  "resi-extinguisher": {
    title: "IN CASE OF EMERGENCY",
    lines: ["break glass.", "then reflect on what led you here."],
  },
  "resi-urn": {
    title: "COFFEE",
    lines: ["decaf since the incident.", "the incident is not discussed."],
  },
  "resi-caddy": {
    title: "CONDIMENTS",
    lines: ["sauce*.", "(*sauce-adjacent)"],
  },
  "resi-microwave": {
    title: "MICROWAVE",
    lines: ["label your fish. — management", "management has stopped labeling their fish."],
  },
  "resi-dartboard": {
    title: "STAFF DART LEAGUE",
    lines: ["season 6.", "undefeated: the wall."],
  },
  "resi-vending": {
    title: "VENDING",
    lines: ["WATER — $6", "SNACKS — $9", "KINDNESS — see vending policy."],
  },
  "resi-espresso": {
    title: "ESPRESSO MACHINE",
    lines: ["the only machine in the building on a maintenance contract."],
  },
  "resi-sofa": {
    title: "SOFA",
    lines: ["reserved in 15-minute increments.", "sign-up sheet lost (2024)."],
  },
  "resi-tissues": {
    title: "TISSUES",
    lines: ["first one complimentary."],
  },
  // ── php ──
  "php-posters": {
    title: "BENEFITS PATHWAY",
    lines: [
      "START → form 7 → form 12 → denial → START.",
      "the arrows are laminated. the laminate has outlasted three administrations.",
    ],
  },
  "php-stamps": {
    title: "THE STAMP RACK",
    lines: ["DENIED — worn smooth.", "RESUBMIT — worn smooth.", "APPROVED — mint. still in shrink wrap."],
  },
  "php-trays": {
    title: "IN / OUT / PENDING",
    lines: [
      "the IN tray is full.",
      "the OUT tray is full of IN.",
      "everything else is PENDING. PENDING is a place now.",
    ],
  },
  "php-board": {
    title: "THE SYLLABUS",
    lines: ["WEEK 1: coping.", "WEEK 2: co-pays.", "WEEK 3: coping with co-pays.", "(subject to billing.)"],
  },
  "php-stool": {
    title: "THE TEACHER'S STOOL",
    lines: ["the only seat with a height advantage.", "ergonomics are earned."],
  },
  "php-attendance": {
    title: "ATTENDANCE",
    lines: ["attendance IS the treatment.", "sign here. and here. initial here."],
  },
  "php-climate": {
    title: "CLIMATE CONTROL",
    lines: ["settings: driver.", "the vents point where the vents point."],
  },
  "php-route": {
    title: "THE SCENIC ROUTE",
    lines: ["today's route: the long way.", "mileage is billable."],
  },
  "php-vandoor": {
    title: "THE SLIDING DOOR",
    lines: ["the handle is decorative.", "exits are a staff function."],
  },
  // ── iop ──
  "iop-cups": {
    title: "THE CUP SHELF",
    lines: ["specimen cups.", "not coffee cups.", "the colors don't mean flavors."],
  },
  "iop-hatch": {
    title: "THE HATCH",
    lines: ["one way.", "no receipts."],
  },
  "iop-clipboard": {
    title: "SIGN-IN",
    lines: ["three times a week, twelve weeks.", "the sheet remembers what you'd rather not."],
  },
  "iop-restroom": {
    title: "THE PROCEDURE",
    lines: ["fourteen steps, taped to the door.", "step one: knock. step fourteen: knock again."],
  },
  "iop-easel": {
    title: "TOPIC TONIGHT",
    lines: ["the easel is blank.", "the topic is attendance. the topic is always attendance."],
  },
  "iop-thermos": {
    title: "COFFEE",
    lines: ["the only anonymous thing here.", "two pumps per person. posted."],
  },
  "iop-staffsign": {
    title: "STAFF PARKING",
    lines: ["closest to the door.", "the spot has tenure."],
  },
  // ── sober living ──
  "sober-chorechart": {
    title: "THE CHORE CHART",
    lines: ["laminated after the disputes.", "every rotation, someone gets toilet week.", "the chart is fair. the chart is final. the chart is watching."],
  },
  "sober-milk": {
    title: "THE SHELF",
    lines: ["every container is labeled.", "every label is contested.", "the milk has three names on it. crossed out. twice."],
  },
  "sober-corkboard": {
    title: "RENT DUE",
    lines: ["friday.", "recovery is priceless. the room is $850."],
  },
  "sober-fan": {
    title: "THE FAN",
    lines: ["won the thermostat war.", "there is no thermostat."],
  },
  "sober-hamper": {
    title: "THE HAMPER",
    lines: ["technically a third roommate.", "pays no rent."],
  },
  "sober-coffeemaker": {
    title: "THE HOUSE POT",
    lines: ["communal.", "whoever takes the last cup makes the next pot.", "nobody has ever made the next pot."],
  },
  "sober-stove": {
    title: "THE STOVE",
    lines: ["burners are labeled 1 through 4.", "burner 3 is on probation."],
  },
  "sober-mirrorpage": {
    title: "POSTED: BATHROOM STANDARDS",
    lines: ["inspected weekly.", "the inspector lives here too.", "the inspector is not exempt. (the inspector is exempt.)"],
  },
  "sober-caddy": {
    title: "SUPPLIES",
    lines: ["provided*.", "(*by the last guy on toilet week)"],
  },
  // ── alumni ──
  "alumni-wall": {
    title: "THE WALL",
    lines: ["everyone up there is doing great.", "updates are not accepted."],
  },
  "alumni-thermo": {
    title: "GIVING BACK",
    lines: ["the goal line has moved four times.", "giving back doesn't move."],
  },
  "alumni-chips": {
    title: "THE BOWL",
    lines: ["one chip per anniversary.", "the bowl is also a donation bowl."],
  },
  "alumni-book": {
    title: "GUEST BOOK",
    lines: ["sign in.", "we'll find you."],
  },
  "alumni-grill": {
    title: "THE GRILL",
    lines: ["donated.", "by you. see thermometer."],
  },
  "alumni-podium": {
    title: "OPEN MIC",
    lines: ["the mic is never off."],
  },
  "detox-painted-exit": {
    title: "FIRE EXIT",
    lines: [
      "Painted over. Same color as the wall. You found it anyway.",
      "“In the event of fire, this door remains committed to its current role.”",
    ],
  },
};

/**
 * The OWNER's tap quips — escalate with the run's moral arc (the branding-
 * desperation ladder). Picked by the scene from current state.
 */
export const OWNER_QUIPS = {
  early: ["Every bed is a story!", "Wellness, profitably. Profitably, wellness."],
  prestige: ["Every bed is a billing code.", "We don't lose patients. We recirculate them."],
  criminal: ["Every bed is load-bearing.", "The second ledger balances the first."],
} as const;

/**
 * The med-window LOBBYIST (Detox): an ambient patient forever at the glass,
 * inventing implausible symptoms. The symptoms are creative, not mocking —
 * the window's stonewall is the punchline (satire rules).
 */
export const MED_WINDOW_SYMPTOMS: readonly string[] = [
  "my blood feels loud",
  "I think my bones are damp",
  "restless arm. just the one.",
  "I can taste Wednesdays",
  "my shadow has a headache",
  "phantom hiccups. inherited.",
  "my teeth itch when I lie down",
  "second heartbeat, somewhere in my leg",
  "the ringing in my ears went flat",
  "I'm allergic to gravity now",
];

/** Ward nurse's rounds mutters — chart-speak is the joke, never the patient. */
export const WARD_NURSE_MUTTERS: readonly string[] = [
  "still breathing. billing.",
  "day 3. charting 'improved.'",
  "vitals: fine. invoice: sent.",
  "hydration: observed.",
];

/** Med-bay nurse's replies: ten symptoms, three answers. The asymmetry IS the stonewall. */
export const MED_NURSE_REPLIES: readonly string[] = ["noted.", "hydrate.", "Tylenol."];

/**
 * Break-room VIGNETTES (Russell): a different staff scene each visit.
 * Actors are existing cast textures; lines alternate between the two
 * positions. Satire rules: complaints indict the SYSTEM, never the patient.
 */
export interface BreakVignette {
  actors: Array<{ tex: string; x: number }>; // x = fraction of width
  /** Lines alternate actor 0, 1, 0, 1... */
  script: readonly string[];
}

export const BREAK_VIGNETTES: readonly BreakVignette[] = [
  {
    // the card game (over the foosball table, obviously)
    actors: [{ tex: "person-orderly", x: 0.38 }, { tex: "person-janitor", x: 0.56 }],
    script: [
      "ante up.",
      "I'm in for a co-pay.",
      "raise you one deductible.",
      "full house.",
      "that's out-of-network.",
    ],
  },
  {
    // the complainers (the complaint is always the system)
    actors: [{ tex: "person-admin", x: 0.2 }, { tex: "person-orderly", x: 0.33 }],
    script: [
      "room 4 asked for seconds.",
      "of dinner?",
      "of therapy.",
      "bill it.",
      "I already did.",
    ],
  },
  {
    // the nap (one actor; the script is a snore)
    actors: [{ tex: "person-janitor", x: 0.22 }],
    script: ["zzz", "zzz…", "zzz"],
  },
  {
    // the session — working here comes with a staff therapist
    actors: [{ tex: "person-therapist", x: 0.36 }, { tex: "person-admin", x: 0.2 }],
    script: [
      "and how does work make you feel?",
      "like a billable hour.",
      "we're making progress.",
      "invoice it.",
      "…already did.",
    ],
  },
];

/**
 * The cafeteria servers' eternal project: serving it before it turns.
 * Alternating lines; the combinations are the crime.
 */
export const SERVER_SPECIALS: readonly string[] = [
  "the tuna turns at noon.",
  "tuna tacos.",
  "with what?",
  "the peach syrup.",
  "we'll call it a compote.",
  "the gravy's due today too.",
  "tuna gravy tacos.",
  "chef's special.",
  "the eggs go tomorrow.",
  "egg salad pizza.",
  "the pizza went Monday.",
  "double special.",
];


/**
 * INSIDE THE DRUGGY BUGGY (Russell, 2026-07-18): the ride is all about
 * ARGUING OVER THE RADIO and crude humor. Riders are BAKED into the approved
 * tableau, so speakers are fixed anchors (fractions of the buggy panel);
 * lines carry their speaker index. Satire rules hold: the squabble is the
 * universal misery of a shared van — the sealed windows and the radio's
 * pecking order — never the riders' diagnoses.
 * Anchor order: 0 sweater(0.30) · 1 phone(0.42) · 2 plaid-back(0.52) ·
 * 3 grandma(0.62) · 4 headphones(0.74) · 5 the unseen DRIVER (front, 0.10).
 */
export const BUGGY_RIDERS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 0.3, y: 0.38 },
  { x: 0.42, y: 0.38 },
  { x: 0.52, y: 0.33 },
  { x: 0.62, y: 0.42 },
  { x: 0.74, y: 0.42 },
  { x: 0.1, y: 0.3 }, // the driver, up front, out of frame
];

export interface BuggyBicker {
  /** [speaker anchor index, line] — played in order, then loops. */
  lines: ReadonlyArray<readonly [number, string]>;
}

export const BUGGY_BICKERS: readonly BuggyBicker[] = [
  {
    // the radio wars
    lines: [
      [3, "who changed the station?"],
      [1, "it's my radio day."],
      [4, "it's ALWAYS your radio day."],
      [1, "check the chart."],
      [3, "driver. the chart."],
      [5, "house rules: static."],
    ],
  },
  {
    // the windows
    lines: [
      [4, "okay. WHO."],
      [2, "own it. whoever you are."],
      [1, "crack a window."],
      [3, "the windows don't open."],
      [4, "why not?!"],
      [5, "liability."],
    ],
  },
  {
    // volume politics
    lines: [
      [1, "turn it UP, I love this one."],
      [3, "turn it DOWN, I hate this one."],
      [5, "compromise."],
      [4, "he muted it again."],
      [2, "he always mutes it."],
      [5, "silence is a genre."],
    ],
  },
  {
    // the smell, unclaimed
    lines: [
      [2, "it's egg salad day, isn't it."],
      [1, "smells like it's ALWAYS egg salad day in here."],
      [3, "that's not the egg salad."],
      [4, "ugh. WHO."],
      [2, "the van has secrets."],
      [5, "the van keeps them."],
    ],
  },
];


/**
 * CASE MANAGEMENT (PHP top floor — Russell, 2026-07-18): the conversation is
 * the patient trying to navigate GOVERNMENT BENEFITS to no avail. Huge levels
 * of red tape. The tableau is frozen, so two fixed anchors: 0 = the PATIENT
 * (seated), 1 = the CASE MANAGER (behind the desk). Satire rules: the maze is
 * the villain — the patient is reasonable, the manager is earnest, the SYSTEM
 * answers every question with a form.
 */
export const CASE_ACTORS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 0.41, y: 0.40 }, // the patient, in the chair
  { x: 0.56, y: 0.33 }, // the case manager, behind the desk
];

export interface CaseRedTape {
  lines: ReadonlyArray<readonly [number, string]>;
}

export const CASE_RED_TAPE: readonly CaseRedTape[] = [
  {
    // the circular forms
    lines: [
      [0, "I just need the benefits form."],
      [1, "that's form 7. form 7 needs form 12."],
      [0, "okay. form 12."],
      [1, "form 12 requires a denial of form 7."],
      [0, "then deny me form 7!"],
      [1, "can't. you never applied."],
    ],
  },
  {
    // proof of residence
    lines: [
      [1, "proof of residence?"],
      [0, "I live HERE."],
      [1, "facilities don't count."],
      [0, "then where do I live?"],
      [1, "form 31 determines that."],
      [0, "…is there a form to get form 31?"],
      [1, "now you're getting it."],
    ],
  },
  {
    // office hours
    lines: [
      [0, "I called the benefits office."],
      [1, "they're open tuesdays."],
      [0, "it IS tuesday."],
      [1, "not fiscal tuesdays."],
      [0, "what's a fiscal tuesday?"],
      [1, "they'd explain it. on a fiscal tuesday."],
    ],
  },
  {
    // the hold music
    lines: [
      [0, "I was on hold for three hours."],
      [1, "that's actually very good."],
      [0, "then it hung up on me."],
      [1, "that's the callback system."],
      [0, "nobody called back."],
      [1, "the callback system is form 44."],
    ],
  },
];


/**
 * The GROUP THERAPIST's lesson (PHP classroom — Russell, 2026-07-18): a very
 * DEMEANING lesson — he talks to grown adults like they are stupid, or don't
 * know what's good for them. Satire rules: the butt is the INSTITUTION's
 * condescension (the treatment industry infantilizing adults), never the
 * patients — they just sit there, being talked down to. Kindergarten
 * register, sticker-chart energy, graded breathing.
 */
export const THERAPIST_LESSONS: readonly string[] = [
  "today's word is 'consequences.' can we SAY 'consequences'?",
  "water is the one we drink. very good.",
  "who remembers what we do when we're sad? we USE our WORDS.",
  "let's try breathing again. in… and… no. IN first.",
  "a sticker for everyone who stayed in their seat.",
  "no, no. grown-up decisions are for the staff.",
  "you're all doing SO well. considering.",
  "hands? one at a time. save some questions for the paid hour.",
  "feelings go in the journal. the journal goes in the bin marked 'feelings.'",
  "your homework: think about what you did.",
  // ── deficiency-as-curriculum (Russell, 2026-07-18): the program's broken
  // things, reframed as learning opportunities. The facility saves money;
  // the lesson plan absorbs the blame.
  "the dishwashers don't work because SOME people never learned dishes. now everyone learns dishes.",
  "there's no hot water. cold showers teach acceptance.",
  "the heat is off because comfort is a crutch.",
  "lunch was small. portion control IS self-control.",
  "the elevator is out. every step is part of your journey.",
  "no wifi. we call that 'presence.'",
];


/**
 * The classroom HAND-RAISERS (PHP): earnest questions from the desks —
 * reasonable adults asking reasonable things into the demeaning-lesson wind.
 * The deflection they get is whatever THERAPIST_LESSONS fires next; the
 * interleave is the joke. Fires from a random OCCUPIED desk only.
 */
export const STUDENT_QUESTIONS: readonly string[] = [
  "is this on the test?",
  "can I go to the bathroom?",
  "my form says 'see form.'",
  "what if coping doesn't work?",
  "is water still the one we drink?",
  "when is this class over?",
  "do questions count as participation?",
];


/**
 * IOP CHECK-IN (Russell's program, 2026-07-19): VERIFICATION THEATER — the
 * Case Manager recites procedure at people she knows by name. The ritual is
 * the joke; the patient is never shamed. Solo loop from the staff sprite.
 */
export const IOP_CHECKIN_LINES: readonly string[] = [
  "ID, please. yes, I know you. ID, please.",
  "co-pay first. recovery second.",
  "the cup goes in the hatch. only the cup.",
  "initial here. date here. twice, in case one of you is lying.",
  "results in 3 to 5 days. the test takes 4 minutes.",
  "temperature strip says you're alive. noted. billed.",
  "sign in. the sign-out sheet is a different clipboard.",
  // ── the UA vein (Russell, 2026-07-19): the mandatory drug test as
  // ceremony — fill lines, temperature strips, blue water, the faucet
  // accommodation. The procedure is the joke; results never are.
  "the cup has a fill line. it believes in you.",
  "temperature strip reads 94 to 100. fresh from the tap is neither.",
  "the toilet water is blue. don't ask. the answer is blue.",
  "don't flush. flushing triggers paperwork.",
  "shy bladder? the faucet runs. that's the whole accommodation.",
  "initials on the lid. date on the side. cup in the hatch.",
  "dilute counts as a do-over. do-overs count.",
];

/**
 * IOP GROUP (after-work exhaustion): everyone came from a shift. Fired from
 * a random OCCUPIED folding chair. The tiredness is universal, never the
 * diagnosis.
 */
export const IOP_GROUP_SIGHS: readonly string[] = [
  "came straight from a double.",
  "I'll share tonight. after this coffee.",
  "third meeting this week. it's tuesday.",
  "my boss thinks I'm at yoga.",
  "the coffee's decaf. we checked. twice.",
  "parked under the light. trust issues.",
  "I have homework from two programs now.",
];


/**
 * The IOP GROUP FACILITATOR (Russell, 2026-07-19: legibility first — the
 * earlier too-clever options would read as a broken game). A real, visible
 * leader running the meeting; lines land on FIRST pass, no decoding. The
 * comedy is the exhausted room they're addressed to.
 */
export const IOP_FACILITATOR_LINES: readonly string[] = [
  "okay. who wants to start?",
  "thank you for sharing. who's next?",
  "let's hear from someone who hasn't gone yet.",
  "there are no wrong answers. there are documented answers.",
  "big feelings tonight. we have until nine.",
  "the coffee is not a talking stick. but okay, go ahead.",
  "let's bring it back to the topic. the topic is attendance.",
];


/**
 * THE GUY IN THE CAR (IOP lot — Russell, 2026-07-19): he's trying to come
 * up with excuses not to go in to group. Drafting them out loud, alone,
 * under the dome light. Universal avoidance, fully sympathetic — the
 * excuses are the ritual everyone in that lot has performed.
 */
export const LOT_EXCUSES: readonly string[] = [
  "car trouble. I'm in the car, but still.",
  "flat tire. no. they walk right past the tires.",
  "one more song.",
  "family emergency. my family IS the emergency. half true.",
  "I could be coming down with something. I feel like I could be.",
  "nobody shares before the half. I'll go in at the half.",
  "count of ten. …new ten.",
  "if the lot light flickers, I go in. best of three.",
];


/**
 * THE CHORE GRUMBLES (Sober Living bathroom — RULED, Russell 2026-07-19:
 * "Grumbles work"). The patient on toilet duty, muttering. The target is
 * the chore-chart economy and accountability-speak — the patient is every
 * roommate who ever drew the short straw.
 */
export const CHORE_GRUMBLES: readonly string[] = [
  "the chart says deep clean. the chart doesn't say with what.",
  "accountability smells like bleach.",
  "toilet week. again. the chart has favorites.",
  "this counts as growth. per the house manager.",
  "somebody in this house has given up on aiming.",
  "rent covers the room. the toilet is 'service work.'",
  "step thirteen: the toilet.",
];


/**
 * THE ROOMMATES (Sober Living bedroom — Russell's two jokes: cramped
 * conditions + one BAD ROOMMATE). Alternating two-hander over the baked
 * tableau: 0 = the offender (asleep, loud in every register), 1 = the
 * sufferer (awake, cataloguing). Crude lane, Buggy precedent; the target
 * is the two-to-a-room economics, never recovery.
 */
export const ROOMMATE_SCRIPT: ReadonlyArray<readonly [number, string]> = [
  [0, "HHHNNK-shoooo."],
  [1, "I counted. eleven distinct sounds."],
  [0, "brrrppt."],
  [1, "twelve."],
  [0, "no officer. these are MY stairs."],
  [1, "he apologizes every morning. sincerely. daily."],
  [0, "mimimimimi."],
  [1, "rent is per bed. the sounds are free."],
];

/**
 * THE HOUSE MANAGER's meeting (Sober Living kitchen): meeting-as-LANDLORD —
 * HOA energy, chore chart, whose milk, rent due. Distinct from IOP
 * facilitation (meeting-as-therapy) and resi platitudes (billing wisdom).
 */
export const HOUSE_MEETING_LINES: readonly string[] = [
  "house meeting to order. first item: the chore chart is not a suggestion.",
  "someone has been labeling other people's food as theirs. bold.",
  "quiet hours start at ten. anger is not quiet.",
  "rent is due friday. recovery is ongoing. rent is due friday.",
  "whoever finished the milk: the meeting can wait. we can all wait.",
  "the thermostat is set where it is set.",
  "last item: the toilet. see the chart. it's laminated now.",
];


/**
 * ALUMNI (scene 7). The TESTIMONIAL: an alum at the BBQ podium, forever
 * mid-story — earnest, endless, loved. The OWNER works the crowd with the
 * booster pitch: the alumni program IS the marketing department, and he
 * knows it even if nobody else says it.
 */
export const ALUMNI_TESTIMONIAL: readonly string[] = [
  "so there I was. rock bottom. part four.",
  "and that's when everything changed. again.",
  "I owe it all to this place. and the van.",
  "long story short — and this IS the short version —",
  "I don't want to take up too much time. anyway. chapter one.",
  "in closing: one more story.",
];

export const OWNER_ALUMNI_PITCH: readonly string[] = [
  "you look great. have you considered giving back?",
  "stay in touch. stay enrolled in something.",
  "the newsletter is free. the reunion is $40.",
  "once a client, always family. terms apply.",
];

/** The facilitator's warped platitudes — recovery-speak, invoice showing. */
export const FACILITATOR_PLATITUDES: readonly string[] = [
  "one day at a time. billed nightly.",
  "you are exactly where you're supposed to be. contractually.",
  "healing isn't linear. neither is the invoice.",
  "let go of what you can't control. like the fees.",
  "progress, not perfection. we bill for both.",
  "the first step is admitting. the second is co-paying.",
  "you can't pour from an empty cup. refills $4.",
  "everything happens for a reason. usually billing.",
];
